import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera as CameraIcon, AlertCircle } from 'lucide-react';

export default function Component() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [backStream, setBackStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isTakingPhoto, setIsTakingPhoto] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [showEmojis, setShowEmojis] = useState(false);
  const emojiRainRef = useRef<number | null>(null);

  // Function to request and set up the back camera stream for preview
  const setupBackCamera = useCallback(async () => {
    try {
      setError(null); // Clear previous errors
      
      // Get list of available devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      // If we have multiple cameras, try to use the back one
      if (videoDevices.length > 1) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { 
              facingMode: { exact: 'environment' }, // Strict requirement for back camera
              width: { ideal: 1280 },
              height: { ideal: 720 }
            },
            audio: false,
          });
          setBackStream(stream);
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
          return; // Exit if successful
        } catch (backCameraErr) {
          console.log('Strict back camera failed, trying alternative approach:', backCameraErr);
        }
      }
      
      // Try a more lenient approach for back camera
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { 
            facingMode: 'environment', // Less strict requirement
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false,
        });
        setBackStream(stream);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        return; // Exit if successful
      } catch (lenientBackErr) {
        console.log('Lenient back camera failed, trying any camera:', lenientBackErr);
      }
      
      // Last resort: use any available camera
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      setBackStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('Camera permission denied. Please allow camera access in your browser settings.');
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          setError('No camera found on this device.');
        } else {
          setError(`Error accessing camera: ${err.message}`);
        }
      } else {
        setError('An unknown error occurred while accessing the camera.');
      }
      setBackStream(null); // Ensure stream is null on error
    }
  }, []);

  // Effect to set up the back camera on mount and clean up
  useEffect(() => {
    // Add a small delay before initializing camera to ensure DOM is fully ready
    const timer = setTimeout(() => {
      setupBackCamera();
    }, 300);

    // Cleanup function to stop the stream when the component unmounts
    return () => {
      clearTimeout(timer);
      backStream?.getTracks().forEach(track => track.stop());
      console.log('Back camera stream stopped.');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Function to capture photo from the front camera
  const handleTakePhoto = async () => {
    if (isTakingPhoto) return; // Prevent multiple rapid clicks

    setIsTakingPhoto(true);
    setError(null); // Clear previous errors
    setShowFlash(true); // Trigger flash effect
    setShowEmojis(false); // Reset emoji state
    window.gizmoRuntime?.performHaptic?.('medium'); // Haptic feedback

    let frontStream: MediaStream | null = null;
    const tempVideo = document.createElement('video');
    tempVideo.setAttribute('playsinline', ''); // Important for iOS
    tempVideo.setAttribute('autoplay', '');
    tempVideo.setAttribute('muted', '');
    tempVideo.style.position = 'fixed'; // Keep off-screen
    tempVideo.style.top = '-9999px';
    tempVideo.style.left = '-9999px';
    document.body.appendChild(tempVideo); // Needs to be in DOM for some browsers

    try {
      // 1. Request front camera stream
      frontStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' }, // 'user' for front camera
        audio: false,
      });

      tempVideo.srcObject = frontStream;

      // 2. Wait for video metadata to ensure dimensions are available
      await new Promise<void>((resolve, reject) => {
        tempVideo.onloadedmetadata = () => {
          tempVideo.play().then(() => {
            // Short delay to allow the stream to stabilize
            setTimeout(resolve, 300);
          }).catch(reject);
        };
        tempVideo.onerror = () => reject(new Error('Error loading front camera video metadata.'));
        // Timeout if metadata doesn't load
        setTimeout(() => reject(new Error('Timeout waiting for front camera metadata.')), 5000);
      });

      // 3. Capture frame onto canvas
      const canvas = document.createElement('canvas');
      canvas.width = tempVideo.videoWidth;
      canvas.height = tempVideo.videoHeight;
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Could not get 2D context from canvas');
      }
      // Flip the image horizontally for a mirror effect (typical for front cameras)
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
      context.drawImage(tempVideo, 0, 0, canvas.width, canvas.height);

      // 4. Get image data URL
      const dataUrl = canvas.toDataURL('image/jpeg');
      setCapturedImage(dataUrl);

      // Start emoji rain after a short delay
      setTimeout(() => {
        setShowEmojis(true);
      }, 500);

    } catch (err) {
      console.error('Error taking photo with front camera:', err);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('Front camera permission denied. Please allow camera access.');
        } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          setError('Front camera not found on this device.');
        } else {
          setError(`Error capturing photo: ${err.message}`);
        }
      } else {
        setError('An unknown error occurred while capturing the photo.');
      }
    } finally {
      // 5. Cleanup: Stop front camera stream and remove temporary elements
      frontStream?.getTracks().forEach(track => track.stop());
      if (tempVideo.parentNode) {
        tempVideo.parentNode.removeChild(tempVideo);
      }
      console.log('Front camera stream stopped.');
      setIsTakingPhoto(false);
      // Flash effect ends after a short duration
      setTimeout(() => setShowFlash(false), 150);
    }
  };

  // Function to discard the captured image and return to camera view
  const handleRetakePhoto = () => {
    setCapturedImage(null);
    setShowEmojis(false);
    window.gizmoRuntime?.performHaptic?.('light');
    
    // Stop emoji animation if running
    if (emojiRainRef.current !== null) {
      cancelAnimationFrame(emojiRainRef.current);
      emojiRainRef.current = null;
    }
  };

  // Emoji rain effect
  useEffect(() => {
    if (!showEmojis || !capturedImage) return;

    // Create emoji rain canvas
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to match container
    const resizeCanvas = () => {
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;
    };
    resizeCanvas();

    // Create emojis
    const emojis = ['😂', '🤣', '😆', '😹', '😄'];
    const emojiSize = Math.min(canvas.width, canvas.height) * 0.08; // Responsive size
    const emojiCount = Math.floor((canvas.width * canvas.height) / 20000); // Responsive count

    interface EmojiParticle {
      x: number;
      y: number;
      emoji: string;
      speed: number;
      rotation: number;
      rotationSpeed: number;
      scale: number;
      opacity: number;
    }

    const particles: EmojiParticle[] = [];

    // Initialize particles
    for (let i = 0; i < emojiCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * -canvas.height, // Start above the canvas
        emoji: emojis[Math.floor(Math.random() * emojis.length)],
        speed: 1 + Math.random() * 3,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.1,
        scale: 0.5 + Math.random() * 0.5,
        opacity: 0.7 + Math.random() * 0.3
      });
    }

    // Draw the captured image on the canvas
    const img = new Image();
    img.onload = () => {
      // Calculate dimensions to maintain aspect ratio
      const imgRatio = img.width / img.height;
      const canvasRatio = canvas.width / canvas.height;
      
      let drawWidth, drawHeight, offsetX = 0, offsetY = 0;
      
      if (imgRatio > canvasRatio) {
        // Image is wider than canvas (relative to height)
        drawHeight = canvas.height;
        drawWidth = drawHeight * imgRatio;
        offsetX = (canvas.width - drawWidth) / 2;
      } else {
        // Image is taller than canvas (relative to width)
        drawWidth = canvas.width;
        drawHeight = drawWidth / imgRatio;
        offsetY = (canvas.height - drawHeight) / 2;
      }
      
      // Animation function
      const animate = () => {
        // Clear canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Draw the image
        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        
        // Draw and update emojis
        particles.forEach(p => {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.scale(p.scale, p.scale);
          ctx.globalAlpha = p.opacity;
          ctx.font = `${emojiSize}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(p.emoji, 0, 0);
          ctx.restore();
          
          // Update position
          p.y += p.speed;
          p.rotation += p.rotationSpeed;
          
          // Reset if off screen
          if (p.y > canvas.height + emojiSize) {
            p.y = -emojiSize;
            p.x = Math.random() * canvas.width;
          }
        });
        
        // Continue animation
        emojiRainRef.current = requestAnimationFrame(animate);
      };
      
      // Start animation
      animate();
      window.gizmoRuntime?.performHaptic?.('soft');
    };
    img.src = capturedImage;

    // Cleanup function
    return () => {
      if (emojiRainRef.current !== null) {
        cancelAnimationFrame(emojiRainRef.current);
        emojiRainRef.current = null;
      }
    };
  }, [showEmojis, capturedImage]);

  return (
    <div className="relative flex items-center justify-center h-screen w-full bg-black overflow-hidden">
      {/* Camera Preview */}
      {!capturedImage && (
        <>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="absolute top-0 left-0 w-full h-full object-cover z-0"
            aria-label="Back camera preview"
          />
          {!backStream && !error && (
            <div className="absolute inset-0 flex items-center justify-center z-10 bg-black/70">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
            </div>
          )}
        </>
      )}

      {/* Captured Image Preview with Emoji Rain */}
      {capturedImage && (
        <div className="absolute top-0 left-0 w-full h-full z-0 flex items-center justify-center bg-black">
          <canvas 
            ref={canvasRef}
            className="w-full h-full object-contain"
            aria-label="Captured photo with laughing emojis"
          />
        </div>
      )}

      {/* Error Message Overlay */}
      {error && (
        <div className="absolute top-4 left-4 right-4 bg-red-600/80 text-white p-3 rounded-lg z-20 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Instructions Overlay removed */}

      {/* Flash Effect Overlay */}
      {showFlash && (
        <div className="absolute inset-0 bg-white opacity-80 z-10 animate-pulse-fast"></div>
      )}

      {/* Camera Controls */}
      {!capturedImage && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <button
            onClick={handleTakePhoto}
            disabled={isTakingPhoto || !backStream || !!error}
            className={`w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg focus:outline-none focus:ring-4 focus:ring-white/50 transition-opacity duration-200 ${
              isTakingPhoto ? 'opacity-50 cursor-not-allowed' : 'opacity-100 hover:bg-gray-200'
            } ${(!backStream || !!error) ? 'opacity-30 cursor-not-allowed' : ''}`}
            aria-label={isTakingPhoto ? 'Capturing...' : 'Take Photo (uses front camera)'}
          >
            <div className="w-16 h-16 rounded-full border-4 border-black flex items-center justify-center">
              <CameraIcon className="w-8 h-8 text-black" />
            </div>
          </button>
        </div>
      )}

      {/* Take Another Photo Button */}
      {capturedImage && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <button
            onClick={handleRetakePhoto}
            className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg focus:outline-none focus:ring-4 focus:ring-white/50 transition-opacity duration-200 hover:bg-gray-200"
            aria-label="Take another photo"
          >
            <div className="w-16 h-16 rounded-full border-4 border-black flex items-center justify-center">
              <CameraIcon className="w-8 h-8 text-black" />
            </div>
          </button>
        </div>
      )}

      {/* Style for flash animation */}
      <style>{`
        @keyframes pulse-fast {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 0.4; }
        }
        .animate-pulse-fast {
          animation: pulse-fast 0.15s ease-in-out;
        }
      `}</style>
    </div>
  );
}



